/* eslint-disable camelcase */
import { useEffect, useState, useContext } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate, useLocation } from "react-router-dom"
import { Box, Paper, TextField, Tooltip, IconButton, Grid, MenuItem, Typography, Divider } from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, useQuery, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"
import Save from "@mui/icons-material/Save"
import GavelIcon from "@mui/icons-material/Gavel"
import DetalleArticulosGrid from "../../CrearSolicitudIngEgr/Crear/components/DetalleArticulosGrid"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1300px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const EditarAprobacionSol = () => {
  const navigate = useNavigate()
  const { state } = useLocation() // Recibimos la fila seleccionada desde la bandeja
  const { selectedMenuInfo } = useContext(GlobalContext)

  // Estado original de la solicitud (Solo lectura)
  const [formData, setFormData] = useState({
    solnumero: "",
    loccodigo: "",
    tsolInEg: "",
    tmotcodigo: "",
    sgadescri: "",
    sgastatus: "PENDIENTE",
    detalle: [],
  })

  // Estado de la resolución del supervisor
  const [resolucion, setResolucion] = useState({
    nuevo_estado: "",
    comentario: "",
  })

  useEffect(() => {
    // Si entran directo a la URL sin pasar por la grilla, los expulsamos a la bandeja
    if (!state?.solnumero) {
      navigate(-1)
    }
  }, [state, navigate])

  // OBTENER DETALLES COMPLETOS DE LA SOLICITUD
  const { data: solData, isLoading: loadSol } = useQuery({
    queryKey: ["getSolicitudByIdApro", state?.solnumero],
    queryFn: async () => {
      const payload = { solnumero: state.solnumero, loccodigo: state.loccodigo || "01" }
      const response = await api.post("/CrearSolicitudIngEgr/getSolicitudById", payload)
      let coreData = null
      const r1 = response?.data
      if (r1?.data?.data?.solnumero) coreData = r1.data.data
      else if (r1?.data?.solnumero) coreData = r1.data
      else if (r1?.solnumero) coreData = r1
      return coreData
    },
    enabled: !!state?.solnumero,
  })

  useEffect(() => {
    if (solData) {
      setFormData({
        ...solData,
        detalle: (solData.detalle || []).map((item) => ({
          ...item,
          idCompuesto: `${item.invcodigo}_${item.artcodigo}`,
        })),
      })
    }
  }, [solData])

  // MUTACIÓN PARA PROCESAR LA APROBACIÓN
  const { mutateAsync: SaveResolucion, isPending: isSaving } = useMutation({
    queryKey: ["isResolvingSolicitud"],
    fn: async (data) => {
      const payload = {
        solnumero: formData.solnumero,
        nuevo_estado: data.nuevo_estado,
        comentario: data.comentario,
      }
      const response = await api.post("/AprobacionSolIngEgr/procesarAprobacion", payload)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: () => navigate(-1), // Retorna a la bandeja tras guardar
  })

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()

    if (!resolucion.nuevo_estado) return showWarning("Debe seleccionar una Acción de Resolución.")
    if (!resolucion.comentario.trim()) return showWarning("El Comentario de Resolución es obligatorio.")

    // Validaciones preventivas de Reglas de Negocio en Frontend
    const totalMovido = formData.detalle.reduce(
      (acc, cur) => acc + parseFloat(cur.sgacanrec || 0) + parseFloat(cur.sgacanegr || 0),
      0,
    )

    if (resolucion.nuevo_estado === "ANULADA" && totalMovido > 0) {
      return showWarning("No puede anular la solicitud. Existen cantidades procesadas en el detalle.")
    }
    if (resolucion.nuevo_estado === "CERRADA" && totalMovido === 0) {
      return showWarning("No puede cerrar la solicitud. Ningún artículo ha sido procesado.")
    }

    await SaveResolucion(resolucion)
  }

  // ACCIÓN DE LA BARRA DE MENÚ (BOTÓN GRABAR)
  const actualizarAction = selectedMenuInfo?.data?.barraAcciones?.find(
    (action) => action?.acccaption === "GRABAR" || action?.acccaption === "PROCESAR",
  )

  const toolbarActions = actualizarAction
    ? [
        {
          label: actualizarAction.acccaption,
          key: actualizarAction.acccaption,
          icon: getIconComponent(actualizarAction.accnameicono, actualizarAction.acctipoico),
        },
      ]
    : [{ label: "Grabar Resolución", key: "GRABAR", icon: <Save /> }]

  const loadingGlobal = isSaving || loadSol

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        {/* Toolbar superior con el botón de Guardar */}
        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <IconButton
                onClick={handleSubmit}
                disabled={loadingGlobal}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, mr: 1, bgcolor: "white" }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Aprobar Solicitud: {state?.solnumero}</b>
        </div>

        <CustomBackdrop isLoading={loadingGlobal} />

        <Box sx={StyledRoot}>
          {/* PANEL DE RESOLUCIÓN (SUPERVISOR) */}
          <Paper
            elevation={4}
            sx={{ p: 4, mb: 4, borderRadius: 3, borderLeft: "6px solid #196C87", background: "#f8fbff" }}
          >
            <Typography
              variant="h6"
              color="primary"
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
            ></Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  label="Acción *"
                  value={resolucion.nuevo_estado}
                  onChange={(e) => setResolucion((prev) => ({ ...prev, nuevo_estado: e.target.value }))}
                >
                  <MenuItem value="APROBADO">APROBAR SOLICITUD</MenuItem>
                  <MenuItem value="ANULADA" sx={{ color: "error.main" }}>
                    ANULAR SOLICITUD
                  </MenuItem>
                  <MenuItem value="CERRADA" sx={{ color: "secondary.main" }}>
                    CERRAR SOLICITUD (PARCIAL)
                  </MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="Comentario de Accion *"
                  value={resolucion.comentario}
                  onChange={(e) => setResolucion((prev) => ({ ...prev, comentario: e.target.value.toUpperCase() }))}
                  inputProps={{ maxLength: 250 }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* DATOS ORIGINALES EN MODO SOLO LECTURA */}
          <Paper elevation={1} sx={{ p: 4, borderRadius: 3, background: "white", opacity: 0.8, pointerEvents: "none" }}>
            <Typography variant="subtitle1" color="textSecondary" gutterBottom>
              DATOS ORIGINALES DE LA SOLICITUD (Solo Lectura)
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Tipo de Solicitud"
                  value={formData.tsolInEg}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Motivo" value={formData.tmotcodigo} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descripción Original"
                  value={formData.sgadescri}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Typography variant="subtitle1" color="textSecondary" gutterBottom>
              DETALLE DE ARTÍCULOS
            </Typography>
            {formData.tsolInEg ? (
              <DetalleArticulosGrid
                detalle={formData.detalle}
                setFormData={() => {}}
                tsolInEg={formData.tsolInEg}
                clicodigo={formData.clicodigo}
              />
            ) : (
              <Typography variant="body2" color="textSecondary" sx={{ fontStyle: "italic", mt: 2 }}>
                Cargando artículos...
              </Typography>
            )}
          </Paper>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default EditarAprobacionSol
